import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CreditCard, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { formatMoney } from "../lib/usageFormat";
import NoticeBanners from "./NoticeBanners";

// Prompt 24 A4 — Settings → Billing.
const STATUS_COPY = {
  free_tier: "Free tier",
  active: "Active · billed monthly",
  needs_card: "Card needed to receive",
  payment_failed: "Payment failed — update your card",
};

export default function BillingPanel() {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [params] = useSearchParams();

  const load = useCallback(() => api.get("/billing/status").then(({ data }) => setB(data))
    .catch((e) => setErr(e?.response?.data?.detail || "Could not load billing")), []);

  useEffect(() => {
    const sid = params.get("session_id");
    if (params.get("setup") === "success" && sid) {
      api.post("/billing/setup-session/confirm", { session_id: sid }).catch(() => {}).finally(load);
    } else {
      load();
    }
  }, [params, load]);

  const go = async (path) => {
    setBusy(true);
    setErr(null);
    try {
      const { data } = await api.post(path, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not open Stripe");
      setBusy(false);
    }
  };

  if (!b) return <div className="card-flat p-6 text-sm text-neutral-500" data-testid="billing-loading">{err || "Loading billing…"}</div>;
  if (b.internal) return <div className="card-flat p-6 text-sm" data-testid="billing-internal">Internal account: not billed</div>;
  const ft = b.free_tier;

  return (
    <section className="card-flat p-6 space-y-5" data-testid="billing-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-mini">billing</div>
          <h2 className="font-display text-xl font-semibold mt-1" data-testid="billing-status">
            {b.configured ? STATUS_COPY[b.status] || b.status : "Billing not set up yet"}
          </h2>
          <p className="text-sm text-neutral-600 mt-1">
            {formatMoney(b.price_per_invoice_usd)} per invoice received into your books after the free tier,
            billed monthly. No monthly minimum. Sending invoices is always free.
          </p>
          {!b.has_payment_method ? (
            <p className="text-sm text-neutral-800 mt-2" data-testid="billing-card-hint">
              Add a card when you're ready to receive invoices.
            </p>
          ) : null}
        </div>
        <span className="text-[10px] uppercase tracking-widest border px-2 py-1 font-mono"
              data-testid="billing-pm-status">
          {b.has_payment_method ? "payment method on file" : "no payment method"}
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
        <div className="border border-neutral-200 p-4">
          <div className="label-mini">free invoices left</div>
          <div className="font-display text-2xl font-semibold mt-1" data-testid="billing-free-left">
            {ft.active ? ft.remaining : 0} / {ft.limit}
          </div>
        </div>
        <div className="border border-neutral-200 p-4">
          <div className="label-mini">free days left</div>
          <div className="font-display text-2xl font-semibold mt-1" data-testid="billing-days-left">
            {ft.active ? ft.days_left : 0}
          </div>
        </div>
      </div>

      <NoticeBanners testid="billing-notice-banners" />

      {b.carried_forward_usd > 0 ? (
        <p className="text-sm text-neutral-800" data-testid="billing-carried-forward">
          Carried forward: {formatMoney(b.carried_forward_usd)} — billed once it reaches {formatMoney(b.bill_threshold_usd ?? 5)}
        </p>
      ) : null}

      {err ? <div className="text-sm text-red-700" data-testid="billing-error">{err}</div> : null}

      {b.configured ? (
        <div className="flex flex-wrap gap-3">
          <button type="button" className="btn-primary flex items-center gap-2" disabled={busy}
                  onClick={() => go("/billing/setup-session")} data-testid="billing-add-pm">
            <CreditCard size={14} /> {b.has_payment_method ? "replace payment method" : "add payment method"}
          </button>
          <button type="button" className="btn-outline flex items-center gap-2" disabled={busy}
                  onClick={() => go("/billing/portal")} data-testid="billing-manage">
            <ExternalLink size={14} /> manage billing
          </button>
        </div>
      ) : (
        <p className="text-sm text-neutral-500" data-testid="billing-not-configured">
          Billing isn't switched on yet. Nothing will be charged until it is.
        </p>
      )}
      <p className="text-xs text-neutral-500">
        Card or US bank account (ACH). Payments are handled by Stripe; invoices and payment
        methods are managed in the Stripe billing portal.
      </p>
    </section>
  );
}
